import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cylindersOf, roundVolume } from "./customRefillCylinders";
import { fetchCustomRefillQuote, fetchProductPackings } from "./detailService";
import { mapCustomRefillQuote } from "./detailMappers";
import { useSupplierProducts } from "./useSupplierBody";

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `refill_${idCounter}`;
};

const MIN_VOLUME = 0.5;
const VOLUME_STEPS = [0.25, 0.5, 0.75, 1];

const useCustomRefill = ({ supplierId, containerOptionId, enabled }) => {
  const grid = useSupplierProducts({ supplierId, containerOptionId, enabled });

  const cylinders = useMemo(() => cylindersOf(grid.products), [grid.products]);
  const largestCylinder = cylinders[cylinders.length - 1] || null;

  const cylinderOf = useCallback(
    (productId) =>
      cylinders.find((cylinder) => cylinder.productId === productId) || null,
    [cylinders]
  );

  const [packings, setPackings] = useState({});
  const requestedPackings = useRef(new Set());

  useEffect(() => {
    cylinders
      .filter((cylinder) => !requestedPackings.current.has(cylinder.productId))
      .forEach((cylinder) => {
        requestedPackings.current.add(cylinder.productId);

        fetchProductPackings(cylinder.productId)
          .then((data) =>
            setPackings((prev) => ({ ...prev, [cylinder.productId]: data?.packings || [] }))
          )
          .catch(() => {});
      });
  }, [cylinders]);

  const materialsOf = useCallback(
    (productId) =>
      productId !== null
        ? packings[productId] || cylinderOf(productId)?.materials || []
        : [],
    [packings, cylinderOf]
  );

  const entryOfCylinder = useCallback(
    (cylinder, id) => {
      const materials = cylinder ? materialsOf(cylinder.productId) : [];
      const material = materials[0] || null;

      return {
        id,
        productId: cylinder?.productId ?? null,
        materialId: material?.id ?? null,
        materialLabel: material?.defaultTitle || material?.code || "",
        cylinderVolume: cylinder?.volume ?? null,
        refillType: "full",
        refillVolume: cylinder?.volume ?? null,
        amount: null,
        currency: cylinder?.currency ?? null,
        quoting: false,
        quoteError: null,
      };
    },
    [materialsOf]
  );

  const [entries, setEntries] = useState([]);
  const [bounds, setBounds] = useState(null);

  useEffect(() => {
    if (!enabled || !largestCylinder) return;

    setEntries((current) =>
      current.length ? current : [entryOfCylinder(largestCylinder, nextId())]
    );
  }, [enabled, largestCylinder, entryOfCylinder]);

  useEffect(() => {
    setEntries((prev) =>
      prev.some((entry) => entry.materialId === null && entry.productId !== null)
        ? prev.map((entry) => {
            if (entry.materialId !== null || entry.productId === null) return entry;

            const material = materialsOf(entry.productId)[0];
            if (!material) return entry;

            return {
              ...entry,
              materialId: material.id,
              materialLabel: material.defaultTitle || material.code || "",
            };
          })
        : prev
    );
  }, [materialsOf]);

  const setEntry = useCallback((entryId, patch) => {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry))
    );
  }, []);

  const quote = useCallback(
    async (entryId, refillVolume) => {
      if (!supplierId || !Number.isFinite(refillVolume) || refillVolume <= 0) return;

      setEntry(entryId, { quoting: true, quoteError: null });

      try {
        const dto = await fetchCustomRefillQuote({ supplierId, refillVolume });
        const result = mapCustomRefillQuote(dto);

        if (result.minRefillVolume !== null && result.maxRefillVolume !== null) {
          setBounds({ min: result.minRefillVolume, max: result.maxRefillVolume });
        }

        setEntry(entryId, {
          quoting: false,
          refillVolume:
            result.refillVolume !== null ? roundVolume(result.refillVolume) : refillVolume,
          amount: result.amount,
          currency: result.currency,
        });
      } catch (error) {
        setEntry(entryId, {
          quoting: false,
          quoteError: "We could not price this refill. Please try a different volume.",
        });
      }
    },
    [supplierId, setEntry]
  );

  const attemptedRef = useRef(new Set());

  useEffect(() => {
    const pending = entries.find(
      (entry) => entry.cylinderVolume !== null && entry.amount === null && !entry.quoting
    );
    if (!pending) return;

    const key = `${pending.id}:${pending.productId}:${pending.refillVolume}`;
    if (attemptedRef.current.has(key)) return;
    attemptedRef.current.add(key);

    quote(pending.id, pending.refillVolume);
  }, [entries, quote]);

  const setCylinder = useCallback(
    (entryId, productId) => {
      const cylinder = cylinderOf(Number(productId));
      if (!cylinder) return;

      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === entryId ? entryOfCylinder(cylinder, entry.id) : entry
        )
      );
    },
    [cylinderOf, entryOfCylinder]
  );

  const setMaterial = useCallback(
    (entryId, materialId) => {
      const entry = entries.find((item) => item.id === entryId);
      if (!entry) return;

      const material = materialsOf(entry.productId).find(
        (item) => String(item.id) === String(materialId)
      );
      if (!material) return;

      setEntry(entryId, {
        materialId: material.id,
        materialLabel: material.defaultTitle || material.code || "",
      });
    },
    [entries, materialsOf, setEntry]
  );

  const setRefillType = useCallback(
    (entryId, refillType) => {
      const entry = entries.find((item) => item.id === entryId);
      if (!entry || entry.cylinderVolume === null) return;

      const target =
        refillType === "full" ? entry.cylinderVolume : roundVolume(entry.cylinderVolume / 2);

      setEntry(entryId, { refillType, amount: null });
      quote(entryId, target);
    },
    [entries, setEntry, quote]
  );

  const setRefillVolume = useCallback(
    (entryId, volume) => {
      const entry = entries.find((item) => item.id === entryId);
      if (!entry || entry.cylinderVolume === null) return;

      const max = Math.min(entry.cylinderVolume, bounds?.max ?? entry.cylinderVolume);
      const min = Math.max(bounds?.min ?? MIN_VOLUME, MIN_VOLUME);
      const clamped = Math.min(max, Math.max(min, roundVolume(volume) ?? min));

      quote(entryId, clamped);
    },
    [entries, bounds, quote]
  );

  const addEntry = useCallback(() => {
    setEntries((prev) => [...prev, entryOfCylinder(largestCylinder, nextId())]);
  }, [entryOfCylinder, largestCylinder]);

  const removeEntry = useCallback((entryId) => {
    setEntries((prev) => (prev.length > 1 ? prev.filter((entry) => entry.id !== entryId) : prev));
  }, []);

  const cylinderOptions = useMemo(
    () => cylinders.map((cylinder) => ({ id: cylinder.productId, label: `${cylinder.volume} Kg` })),
    [cylinders]
  );

  const materialOptionsOf = useCallback(
    (entryId) => {
      const entry = entries.find((item) => item.id === entryId);
      if (!entry) return [];

      return materialsOf(entry.productId).map((material) => ({
        id: material.id,
        label: material.defaultTitle || material.code || "",
      }));
    },
    [entries, materialsOf]
  );

  const refillVolumeOptionsOf = useCallback(
    (entryId) => {
      const entry = entries.find((item) => item.id === entryId);
      if (!entry || entry.cylinderVolume === null) return [];

      const max = Math.min(entry.cylinderVolume, bounds?.max ?? entry.cylinderVolume);
      const min = Math.max(bounds?.min ?? MIN_VOLUME, MIN_VOLUME);

      const steps = VOLUME_STEPS.map((step) => roundVolume(entry.cylinderVolume * step)).filter(
        (volume) => volume >= min && volume <= max
      );

      if (entry.refillVolume !== null && !steps.includes(entry.refillVolume)) {
        steps.push(entry.refillVolume);
      }

      return Array.from(new Set(steps))
        .sort((a, b) => a - b)
        .map((volume) => ({ value: volume, label: `${volume} Kg` }));
    },
    [entries, bounds]
  );

  const total = useMemo(() => {
    const priced = entries.filter((entry) => Number.isFinite(entry.amount));
    if (!priced.length) return null;

    return {
      amount: priced.reduce((sum, entry) => sum + entry.amount, 0),
      currency: priced[0].currency,
    };
  }, [entries]);

  const hasCylinders = cylinders.length > 0;

  const canSubmit =
    hasCylinders &&
    entries.some(
      (entry) => entry.productId !== null && entry.refillVolume !== null && Number.isFinite(entry.amount)
    );

  const cartEntries = useMemo(
    () =>
      entries
        .filter((entry) => entry.productId !== null && entry.refillVolume !== null)
        .map((entry) => ({
          productId: entry.productId,
          supplierId,
          quantity: 1,
          containerMaterialId: entry.materialId,
          volume: entry.refillVolume,
          variableVolume: true,
        })),
    [entries, supplierId]
  );

  return {
    isLoading: grid.isLoading,
    hasCylinders,
    entries,
    cylinderOptions,
    materialOptionsOf,
    refillVolumeOptionsOf,
    bounds,
    total,
    canSubmit,
    cartEntries,
    setCylinder,
    setMaterial,
    setRefillType,
    setRefillVolume,
    addEntry,
    removeEntry,
  };
};

export default useCustomRefill;
